// Pure functions that translate local DOM input into remote input messages.
// Kept free of side effects so they can be unit-tested.
import type { InputMsg } from './protocol';

export const MOD_ALT = 1;
export const MOD_CTRL = 2;
export const MOD_META = 4;
export const MOD_SHIFT = 8;

export interface KeyLike {
  key: string;
  code: string;
  keyCode: number;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  repeat: boolean;
  location: number;
  isComposing?: boolean;
}

export const isMacPlatform = (platform = typeof navigator !== 'undefined' ? navigator.platform : '') => /Mac|iPhone|iPad|iPod/i.test(platform);

/**
 * Modifier bitmask for the remote (Linux) Chromium. On macOS the Cmd key is mapped
 * to Ctrl so that Cmd+C / Cmd+V / Cmd+A behave as the user expects.
 */
export function modifiers(e: Pick<KeyLike, 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'>, mac = isMacPlatform()): number {
  let m = 0;
  if (e.altKey) m |= MOD_ALT;
  if (e.ctrlKey) m |= MOD_CTRL;
  if (e.metaKey) m |= mac ? MOD_CTRL : MOD_META;
  if (e.shiftKey) m |= MOD_SHIFT;
  return m;
}

const META_TO_CTRL: Record<string, [string, string, number]> = {
  Meta: ['Control', 'ControlLeft', 17],
};

/** Converts a DOM keyboard event into a remote key message. Returns null for IME composition events. */
export function keyMessage(e: KeyLike, down: boolean, mac = isMacPlatform()): Extract<InputMsg, { t: 'kd' | 'ku' }> | null {
  if (e.isComposing || e.key === 'Process' || e.keyCode === 229) return null;
  if (e.key === 'Unidentified' || e.key === 'Dead') return null;
  let { key, code, keyCode } = e;
  if (mac && META_TO_CTRL[key]) {
    [key, code, keyCode] = META_TO_CTRL[key];
    code = e.code === 'MetaRight' ? 'ControlRight' : code;
  }
  const msg: Extract<InputMsg, { t: 'kd' | 'ku' }> = { t: down ? 'kd' : 'ku', key, code, kc: keyCode, m: modifiers(e, mac) };
  if (e.repeat && down) msg.rep = true;
  if (e.location) msg.loc = e.location;
  return msg;
}

export type LocalShortcut = 'newTab' | 'closeTab' | 'focusAddress' | 'reload' | 'back' | 'forward' | 'nextTab' | 'prevTab' | null;

/** Browser-UI shortcuts handled by the cloud browser chrome instead of the remote page. */
export function localShortcut(e: KeyLike, mac = isMacPlatform()): LocalShortcut {
  const primary = mac ? e.metaKey : e.ctrlKey;
  const k = e.key.toLowerCase();
  if (primary && !e.altKey && !e.shiftKey) {
    if (k === 't') return 'newTab';
    if (k === 'w') return 'closeTab';
    if (k === 'l') return 'focusAddress';
    if (k === 'r') return 'reload';
  }
  if (e.ctrlKey && e.key === 'Tab') return e.shiftKey ? 'prevTab' : 'nextTab';
  if (e.key === 'F5') return 'reload';
  if (e.altKey && !e.ctrlKey && !e.metaKey) {
    if (e.key === 'ArrowLeft') return 'back';
    if (e.key === 'ArrowRight') return 'forward';
  }
  if (mac && e.metaKey && (e.key === '[' || e.key === 'ArrowLeft')) return 'back';
  if (mac && e.metaKey && (e.key === ']' || e.key === 'ArrowRight')) return 'forward';
  return null;
}

/** True for Ctrl/Cmd+V – handled through the local paste event to sync the clipboard first. */
export function isPasteShortcut(e: KeyLike, mac = isMacPlatform()): boolean {
  return (mac ? e.metaKey : e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'v';
}

/** Normalizes WheelEvent deltas to pixels. */
export function wheelDelta(e: { deltaX: number; deltaY: number; deltaMode: number }, pageHeight = 800): { dx: number; dy: number } {
  const f = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? pageHeight : 1;
  return { dx: e.deltaX * f, dy: e.deltaY * f };
}

/** Maps a point in the local video element to remote viewport CSS coordinates. */
export function mapPoint(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  remote: { width: number; height: number },
): { x: number; y: number } {
  const x = ((clientX - rect.left) / Math.max(1, rect.width)) * remote.width;
  const y = ((clientY - rect.top) / Math.max(1, rect.height)) * remote.height;
  return {
    x: Math.round(Math.min(Math.max(x, 0), remote.width - 1) * 100) / 100,
    y: Math.round(Math.min(Math.max(y, 0), remote.height - 1) * 100) / 100,
  };
}

/**
 * Mobile keyboards (autocorrect, predictive text, Android's keyCode 229) are handled
 * by diffing the content of a hidden textarea: returns how many characters to delete
 * (Backspace) and which text to insert so the remote matches the new local value.
 */
export function textDiff(prev: string, next: string): { backspaces: number; insert: string } {
  const a = Array.from(prev);
  const b = Array.from(next);
  let p = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p++;
  return { backspaces: a.length - p, insert: b.slice(p).join('') };
}
